import Api, { handleData, handleError } from '../Api';
import request from '../request';

import { url } from './config';
import { Voucher } from './model';
import {
    AddDocumentBody,
    BalanceGroupTravelFileBody,
    CancelVoucherBody,
    EditVoucherBody,
    GetTravelFileTotalAccountingBody,
    ReconcileVouchersBody,
    UnreconcileVoucherBody
} from './context/context';

class ExtendedApi extends Api<Voucher> {
    // Get by ID - returns info as object (preferred over getOne for single doc fetch)
    getById(id: string) {
        return request.get(`${url}/${id}`).then(handleData).catch(handleError);
    }
    addDocument(body: AddDocumentBody) {
        return request.post(`${url}-add`, body).then(handleData).catch(handleError);
    }
    balanceGroupTravelFile(body: BalanceGroupTravelFileBody) {
        return request.post(`${url}-balance-group-travel-file`, body).then(handleData).catch(handleError);
    }
    getTravelFilesTotalAccounting(body: GetTravelFileTotalAccountingBody) {
        return request
            .post(`${url}-get-travel-files-total-accounting`, body)
            .then(handleData)
            .catch(handleError);
    }
    amendVoucher(body: EditVoucherBody) {
        return request.put(`${url}-edit`, body).then(handleData).catch(handleError);
    }
    updateDocumentInfo(body: { _id: string; fields: Array<{ field: string; value: any }> }) {
        return request.put(`${url}-update-document-info`, body).then(handleData).catch(handleError);
    }
    cancelVoucher(body: CancelVoucherBody) {
        return request.put(`${url}-cancel`, body).then(handleData).catch(handleError);
    }
    reconcileDocuments(body: ReconcileVouchersBody) {
        return request.put(`${url}-reconcile`, body).then(handleData).catch(handleError);
    }
    unreconcileDocument(body: UnreconcileVoucherBody) {
        return request.put(`${url}-unreconcile`, body).then(handleData).catch(handleError);
    }
    query(query: any) {
        return request.get(`${url}-query`, { params: query }).then(handleData).catch(handleError);
    }
    getTurnoverDocuments(query: any) {
        return request.get(`${url}-turnover`, { params: query }).then(handleData).catch(handleError);
    }
    getTurnoverSummaryDocuments(query: any) {
        return request.get(`${url}-turnover-summary`, { params: query }).then(handleData).catch(handleError);
    }

    /**
     * Upload PDF file to S3 storage
     * @param documentId - The document ID
     * @param pdfBlob - The PDF file as Blob
     */
    uploadPdf(documentId: string, pdfBlob: Blob) {
        const formData = new FormData();
        // Create a File with explicit PDF mimetype
        const pdfFile = new File([pdfBlob], 'document.pdf', { type: 'application/pdf' });
        formData.append('pdf', pdfFile);

        // Don't set Content-Type header - let axios set it with the boundary
        return request
            .post(`${url}/${documentId}/upload-pdf`, formData)
            .then(handleData)
            .catch(handleError);
    }

    /**
     * Get signed URL for viewing a stored PDF
     * @param documentId - The document ID
     */
    getPdfUrl(documentId: string) {
        return request.get(`${url}/${documentId}/pdf`).then(handleData).catch(handleError);
    }

    /** The language the customer paid in: 'he' | 'en', or null when unknown. */
    getReceiptLanguage(documentId: string) {
        return request.get(`${url}/${documentId}/receipt-language`).then(handleData).catch(handleError);
    }

    approveDocument(documentId: string, clientId: number) {
        return request.put(`${url}-approve`, { documentId, clientId }).then(handleData).catch(handleError);
    }

    rejectDocument(documentId: string, clientId: number, rejectionReason?: string) {
        return request.put(`${url}-reject`, { documentId, clientId, rejectionReason }).then(handleData).catch(handleError);
    }

    markVouchersAsExported(ids: Array<string | number>) {
        return request.put(`${url}-mark-exported`, { ids }).then(handleData).catch(handleError);
    }

    enrichGlAccounts(voucherIds: Array<string | number>) {
        return request.post(`${url}-gl-accounts`, { voucherIds }).then(handleData).catch(handleError);
    }

    getDocumentPostings(voucherIds: Array<string | number>) {
        return request.post(`${url}-postings`, { voucherIds }).then(handleData).catch(handleError);
    }

    getLedgerReversals(body: {
        toCreatedAt: string;
        fromCreatedAt?: string;
        includeExported?: boolean;
        documentIds?: Array<string | number>;
    }) {
        return request.post(`${url}-ledger-reversals`, body).then(handleData).catch(handleError);
    }

    markLedgerReversalsExported(ids: Array<string | number>) {
        return request.put(`${url}-ledger-reversals-mark`, { ids }).then(handleData).catch(handleError);
    }

    getLedgerHeldBack(body: { toCreatedAt: string; fromCreatedAt?: string; stamp?: boolean }) {
        return request.post(`${url}-ledger-held-back`, body).then(handleData).catch(handleError);
    }

    getDocumentMessages(documentId: string | number) {
        return request.get(`${url}-messages/${documentId}`).then(handleData).catch(handleError);
    }

    addDocumentMessage(documentId: string | number, message: string, action?: string) {
        return request.post(`${url}-messages`, { documentId, message, action }).then(handleData).catch(handleError);
    }

    getAppConfig(key: string) {
        return request.get(`/settings-app-config/${key}`).then(handleData).catch(handleError);
    }

    setAppConfig(key: string, value: string) {
        return request.put(`/settings-app-config/${key}`, { value }).then(handleData).catch(handleError);
    }
}

const api = new ExtendedApi(url);

export default api;
